import { DETECTION_DEFAULT_MODEL_LABEL } from "@/lib/llmModels";

/**
 * The prompts the Prompt Lab edits, and the content fields each one stores.
 * Field names match the backend's content shapes (prompt_catalog.py).
 */

export type PromptField = {
  name: string;
  label: string;
  rows?: number;
  /** May be left empty (sent as null). */
  optional?: boolean;
  /** Rendered as a model picker instead of a text area. */
  input?: "model";
};

export type PromptTargetSpec = {
  target: string;
  label: string;
  fields: PromptField[];
  /** Rendered as a Jinja template: gets a preview, cannot apply legacy versions. */
  template: boolean;
  help?: string;
  outputContract?: string;
  /** Execution pins an explicitly selected version; active is only an admin marker. */
  explicitSelection?: boolean;
  /** The model picker offers every enabled registry model the 1차 scan can use, not only Gemini. */
  detectorModels?: boolean;
  /** Stored reviewer models apply only to Gemini; routed execution is selected separately. */
  reviewerPrompt?: boolean;
  /** Reuse the supported registry transports without scan-specific labels. */
  registryModels?: boolean;
  /** Preview and validate generated criteria through the server. */
  criteriaPreview?: boolean;
  /** Label of the picker's empty choice (default: the server's Gemini default). */
  defaultModelLabel?: string;
};

const BODY: PromptField = { name: "body", label: "Template", rows: 18 };

const RETIRED_SECOND_STAGE = "이전 방식(2차) 기록입니다. 지금은 1차 + 1.1차 통과가 바로 TC가 되므로 실행하지 않습니다. 다시 쓸 때를 위해 버전 저장·활성 표시·삭제는 그대로 할 수 있습니다.";

export const PROMPT_TARGETS: PromptTargetSpec[] = [
  {
    target: "base_single",
    label: "Base prompt · single",
    fields: [BODY],
    template: true,
    help: "1:1 chats. Variables: authenticated_email, sender_email, members_roster.",
  },
  {
    target: "base_group",
    label: "Base prompt · group",
    fields: [BODY],
    template: true,
    help: "Group chats; whisper turns render the {% if whisper %} branches.",
  },
  {
    target: "tool_guideline",
    label: "Tool instruction",
    fields: [BODY],
    template: true,
    help: "Sections are macros; each agent gets the sections its role and tools select.",
  },
  {
    target: "system_tail",
    label: "System prompt tail",
    fields: [BODY],
    template: true,
    help: "Everything after the base prompt: guideline, policies, orchestration, skills, runtime context.",
  },
  {
    target: "personal_agent_policy",
    label: "Personal-agent policies",
    fields: [BODY],
    template: true,
    help: "Included by the tail for 1:1 chats and whisper turns.",
  },
  {
    target: "executor_prompt",
    label: "Sub-agent executor prompt",
    fields: [BODY],
    template: true,
    help: "How a web/library/browser executor's system prompt is assembled.",
  },
  {
    target: "triage",
    label: "Triage prompt",
    fields: [
      { name: "body", label: "Group participation gate prompt", rows: 14 },
      { name: "personal_body", label: "1:1 router prompt", rows: 14 },
      { name: "abstain_reminder", label: "Group abstain reminder", rows: 5, optional: true },
    ],
    template: false,
    help: "The three bodies are one version: they are saved and applied together.",
  },
  {
    target: "issue_signal_detector_item",
    label: "1차 신호 탐지 · 기본 모델",
    fields: [
      { name: "body", label: "Instruction", rows: 16 },
      { name: "model", label: "1차 기본 모델 (매일 02:00 자동 실행도 사용)", optional: true, input: "model" },
    ],
    template: false,
    outputContract: "user_signal_item_v1",
    detectorModels: true,
    defaultModelLabel: DETECTION_DEFAULT_MODEL_LABEL,
    help: "1차 모델을 레지스트리 목록에서 선택합니다. 모델 공란은 서버 기본 Claude CLI(claude-opus-5-5)·Qwen qwen3.8-27b 예비를 사용합니다. 저장된 모델은 실행별 선택이 없을 때 우선하며, 실제 적용 모델은 서버 설정·미리보기에서 확인하세요.",
  },
  {
    target: "issue_signal_detector_compact",
    label: "User signal detector · compact v2",
    fields: [{ name: "body", label: "Instruction", rows: 16 }],
    template: false,
    outputContract: "user_signals_v2",
    help: "1차 모델이 Gemini일 때 쓰는 묶음 프롬프트입니다. 방·스레드를 구분한 짧은 m번호와 화자 별칭으로 사용자 불편 신호를 찾습니다. 긴 발화는 앞 200자·뒤 100자이며, 1.1차는 원문을 다시 조회합니다. 모델은 ‘1차 신호 탐지 · 기본 모델’에서 정합니다.",
  },
  {
    target: "issue_signal_detector",
    label: "User signal detector · legacy v1",
    fields: [{ name: "body", label: "Instruction", rows: 16 }, { name: "model", label: "Model", optional: true, input: "model" }],
    template: false,
    help: "사용자 발화의 불편 신호를 찾는 첫 단계입니다. 관련 답변 검토는 별도 프롬프트를 사용합니다.",
  },
  {
    target: "issue_feedback_reviewer",
    label: "AI feedback reviewer",
    fields: [{ name: "body", label: "Instruction", rows: 16 }, { name: "model", label: "Model", optional: true, input: "model" }],
    template: false,
    outputContract: "user_feedback_v3",
    reviewerPrompt: true,
    defaultModelLabel: DETECTION_DEFAULT_MODEL_LABEL,
    help: "1.1차에서 실제 불편 여부를 확인하고 사용자 👎와 같은 분류의 AI 피드백을 작성합니다. TC 적합성은 심사하지 않습니다.",
  },
  {
    target: "issue_feedback_reviewer_batch",
    label: "AI 피드백 작성 · 신호 묶음",
    fields: [{ name: "body", label: "Instruction", rows: 16 }, { name: "model", label: "Model", optional: true, input: "model" }],
    template: false,
    outputContract: "user_feedback_batch_v4",
    reviewerPrompt: true,
    defaultModelLabel: DETECTION_DEFAULT_MODEL_LABEL,
    help: "1.1차 묶음 검토용 프롬프트입니다. Claude CLI 기본 경로는 신호 1건씩 검토합니다. 각 신호의 실제 불편 여부와 근거를 보존하며 TC 적합성을 심사하지 않습니다.",
  },
  {
    target: "eval_resolution_criteria_generator",
    label: "판단 기준 생성기",
    fields: [
      { name: "body", label: "Instruction", rows: 18 },
      { name: "model", label: "판단 기준 생성 모델", optional: true, input: "model" },
    ],
    template: false,
    outputContract: "resolution_criteria_v1",
    registryModels: true,
    criteriaPreview: true,
    defaultModelLabel: "서버 기본 설정 (GEMINI_SIMPLE_REPLY_MODEL)",
    help: "사용자 👎와 AI 탐지 자동 TC가 같은 활성 프롬프트·모델을 사용합니다. 모델을 비워 두면 기존 서버 기본값을 사용합니다. 출력은 3~6개 checks, 7개 평가 축의 criteria_name, score_impact 합계 1.0을 따라야 합니다. 실패 턴의 고정 기준은 별도로 유지됩니다.",
  },
  {
    target: "issue_signal_reviewer",
    label: "Legacy signal context reviewer",
    fields: [{ name: "body", label: "Instruction", rows: 16 }, { name: "model", label: "Model", optional: true, input: "model" }],
    template: false,
    help: "이전 TC 적합성 심사 프롬프트입니다. 이력 열람용이며 현재 1.1차는 AI feedback reviewer를 사용합니다.",
  },
  ...[
    ["eval_problem_identification", "이전 방식(2차) · 2-A 문제 식별", "problem_identification_v1"],
    ["eval_generalization", "이전 방식(2차) · 2-A 일반화 생성", "generalization_v2"],
    ["eval_reproduction_judgment", "이전 방식(2차) · 2-B 동일 문제 재현 판정", "reproduction_judgment_v1"],
    ["eval_improvement_judgment", "이전 방식(2차) · 2-C Reference 개선 비교", "improvement_judgment_v1"],
  ].map(([target, label, outputContract]): PromptTargetSpec => ({
    target, label, outputContract, template: false, explicitSelection: true,
    fields: [{ name: "body", label: "Instruction", rows: 16 }, { name: "model", label: "Model", optional: true, input: "model" }],
    help: RETIRED_SECOND_STAGE,
  })),
  {
    target: "eval_reference_answer",
    label: "이전 방식(2차) · 2-C Reference 답변 생성",
    fields: [{ name: "body", label: "Instruction", rows: 16 }],
    template: false,
    outputContract: "reference_answer_v1",
    explicitSelection: true,
    help: `${RETIRED_SECOND_STAGE} 2차 검증에서 Reference 답변에 덧붙이던 지침이며, Reference Answer 화면의 생성에는 쓰이지 않습니다.`,
  },
  {
    target: "issue_candidate_detector",
    label: "Legacy issue candidate detector",
    fields: [
      { name: "body", label: "Instruction", rows: 16 },
      { name: "model", label: "Model", optional: true, input: "model" },
    ],
    template: false,
    help: "이전 전체 대화 탐지의 보존된 프롬프트입니다. 새 사용자 신호 탐지에서는 사용하지 않습니다. 분석에 사용한 버전이 기록되며, 후보의 TC 승격은 사람 검토가 필요합니다.",
  },
  {
    target: "change_summary",
    label: "Change-summary instruction",
    fields: [
      { name: "body", label: "Instruction", rows: 12 },
      { name: "model", label: "Model", optional: true, input: "model" },
    ],
    template: false,
    help: "What the model is told when it summarizes a saved version against its base.",
  },
];

export function specFor(target: string): PromptTargetSpec {
  return (
    PROMPT_TARGETS.find((spec) => spec.target === target) ?? {
      target,
      label: target,
      fields: [BODY],
      template: false,
    }
  );
}

export const PREPARED_SESSION_SPEC: PromptTargetSpec = {
  target: "prepared_session",
  label: "Agent prompt",
  fields: [{ name: "body", label: "Prompt body", rows: 18 }],
  template: false,
};
